import { FC } from "react";
import {
  Combobox,
  ComboboxChevron,
  Skeleton,
  Stack,
  Text,
  UnstyledButton,
  useCombobox,
} from "@mantine/core";

import styles from "./UnstyledSelect.module.css";

interface UnstyledSelect {
  value: string;
  onChange: (value: string) => void;
  data: { label: string; value: string; description?: string }[];
  withinPortal?: boolean;
  isLoading?: boolean;
}

export const UnstyledSelect: FC<UnstyledSelect> = ({
  value,
  onChange,
  data,
  withinPortal = true,
  isLoading = false,
}) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const options = data.map(({ label, value, description }) => (
    <Combobox.Option value={value} key={value} p="xs">
      <Stack gap="4px">
        <Text inherit>{label}</Text>
        {description && (
          <Text c="gray.6" lh="xs" fz="xs">
            {description}
          </Text>
        )}
      </Stack>
    </Combobox.Option>
  ));

  const displayValue =
    data.find((option) => option.value === value)?.label ?? "";

  if (isLoading) {
    return <Skeleton height={8} radius="xl" w="40px" display="inline-flex" />;
  }

  return (
    <Combobox
      store={combobox}
      width={300}
      position="bottom-start"
      onOptionSubmit={(value) => {
        onChange(value);
        combobox.closeDropdown();
      }}
      withinPortal={withinPortal}
    >
      <Combobox.DropdownTarget>
        <UnstyledButton
          onClick={() => {
            combobox.openDropdown();
          }}
          fz="inherit"
          className={styles.button}
        >
          {displayValue}{" "}
          <ComboboxChevron className={styles.chevron} size="xs" />
        </UnstyledButton>
      </Combobox.DropdownTarget>
      <Combobox.Dropdown w={200} onClick={(e) => e.preventDefault()}>
        <Combobox.Options>{options}</Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
